<template>
    <form-repeater
        :repeats="repeats"
        :heading="heading"
        :add="$t('forms.auth.practice.microcopy.locations_repeater.add')"
        :hint="hint"
        :remove="$t('forms.auth.practice.microcopy.locations_repeater.remove')"
        :isDisabled="isDisabled"
        @add="addRepeat()"
        @remove="removeRepeat($event)"
    >
        <template #default="{ data, index }">
            <form-wrapper :isSubmittable="false">
                <!-- We hide "Name" for the first location, as it's currently not supported by DB schema. May change in the future. -->

                <form-row>
                    <form-cell v-if="index > 0" width="half">
                        <text-field
                            :label="nameLabel(index)"
                            :placeholder="namePlaceholder(index)"
                            :hasAutofocus="hasAutofocus"
                            :isRequired="!isDisabled"
                            :isDisabled="isDisabled"
                            :formData="data"
                            :errorPath="valuePathField(index, 'name')"
                            :valuePath="valuePathField(index, 'name')"
                            :formRepeater="true"
                            @change="changeRepeat(index, $event)"
                        ></text-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell>
                        <suggest-field
                            :label="getLocationLabel(index)"
                            :placeholder="$t('forms.auth.practice.fields.location_address.placeholder')"
                            :suggestionsCall="addressSuggestionCall"
                            :isRequired="true"
                            :isDisabled="isDisabled"
                            :isSuggested="false"
                            :formData="data"
                            :errorPath="valuePathField(index, 'address')"
                            :valuePath="valuePathField(index, 'address')"
                            :formRepeater="true"
                            @change="changeRepeat(index, $event)"
                            @select="changeLocationAddress($event, index)"
                        ></suggest-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell>
                        <text-field
                            :label="$t('forms.auth.practice.fields.location_city.label')"
                            :placeholder="$t('forms.auth.practice.fields.location_city.placeholder')"
                            :isRequired="!isDisabled"
                            :isDisabled="isDisabled"
                            :isSuggested="false"
                            :formData="data"
                            :errorPath="valuePathField(index, 'city')"
                            :valuePath="valuePathField(index, 'city')"
                            :formRepeater="true"
                            @change="changeRepeat(index, $event)"
                        ></text-field>
                    </form-cell>
                </form-row>
                <form-row>
                    <form-cell width="half">
                        <select-field
                            :label="$t('forms.auth.practice.fields.location_state.label')"
                            :placeholder="$t('forms.auth.practice.fields.location_state.placeholder')"
                            :options="states"
                            :isRequired="!isDisabled"
                            :isDisabled="isDisabled"
                            :isSuggested="false"
                            :formData="data"
                            :errorPath="valuePathField(index, 'state')"
                            :valuePath="valuePathField(index, 'state')"
                            :formPrefills="formPrefills"
                            :formRepeater="true"
                            @change="changeRepeat(index, $event)"
                        ></select-field>
                    </form-cell>
                    <form-cell width="half">
                        <text-field
                            :label="$t('forms.auth.practice.fields.location_postcode.label')"
                            :placeholder="$t('forms.auth.practice.fields.location_postcode.placeholder')"
                            :isRequired="!isDisabled"
                            :isDisabled="isDisabled"
                            :isSuggested="false"
                            :formData="data"
                            :errorPath="valuePathField(index, 'zip')"
                            :valuePath="valuePathField(index, 'zip')"
                            :formRepeater="true"
                            @change="changeRepeat(index, $event)"
                        ></text-field>
                    </form-cell>
                </form-row>
            </form-wrapper>
        </template>
    </form-repeater>
</template>

<script>
    import FormFragmentMixin from 'mixins/FormFragmentMixin'
    import FormRepeaterMixin from 'mixins/FormRepeaterMixin'
    import FormDisableableMixin from 'mixins/FormDisableableMixin'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [FormFragmentMixin, FormRepeaterMixin, FormDisableableMixin],

        props: {
            isUpdatingLocation: { type: Boolean, default: false },
            showAddLaterLocationHint: { type: Boolean, default: true },
            states: { type: Array, required: true },
            hasAutofocus: { type: Boolean, default: false },
        },

        data() {
            return {
                isSuggested: false,
                formPrefills: {},
                autocompleteService: null,
            }
        },

        computed: {
            heading() {
                return this.isUpdatingLocation
                    ? this.$t('forms.auth.practice.microcopy.locations_repeater_update.heading')
                    : this.$t('forms.auth.practice.microcopy.locations_repeater.heading')
            },

            hint() {
                return this.isUpdatingLocation
                    ? this.$t('forms.auth.practice.microcopy.locations_repeater_update.hint')
                    : this.$t('forms.auth.practice.microcopy.locations_repeater.hint')
            },

            addressSuggestionCall() {
                //eslint-disable-next-line no-undef
                const autocompleteService = new google.maps.places.AutocompleteService()
                return (query) => {
                    const request = {
                        input: query,
                        componentRestrictions: { country: 'US' },
                    }
                    return autocompleteService.getPlacePredictions(request).then((results) => {
                        return results?.predictions?.map((item) => {
                            return {
                                label: item.description,
                                value: item.place_id,
                            }
                        })
                    })
                }
            },
        },

        methods: {
            nameLabel(index) {
                return index === 0
                    ? this.$t('forms.auth.practice.fields.location_name.label.primary')
                    : this.$t('forms.auth.practice.fields.location_name.label.secondary')
            },

            namePlaceholder(index) {
                return index === 0
                    ? this.$t('forms.auth.practice.fields.location_name.placeholder.primary')
                    : this.$t('forms.auth.practice.fields.location_name.placeholder.secondary')
            },

            valuePathField(index, fieldValuePath) {
                return index > 0 ? `${this.valuePath}.${index - 1}.${fieldValuePath}` : fieldValuePath
            },

            getLocationLabel(index) {
                return index === 0
                    ? this.$t('forms.auth.practice.fields.location_address.main_location')
                    : this.$t('forms.auth.practice.fields.location_address.label')
            },

            changeLocationAddress(payload, index) {
                if (!payload?.value) {
                    return
                }
                //eslint-disable-next-line no-undef
                const placesService = new google.maps.places.PlacesService(document.createElement('div'))
                const request = {
                    placeId: payload.value,
                    fields: ['address_components'],
                }
                placesService.getDetails(request, (place, status) => {
                    //eslint-disable-next-line no-undef
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                        const addressComponents = {}
                        place.address_components.forEach((component) => {
                            component.types.forEach((type) => {
                                switch (type) {
                                    case 'street_number':
                                        addressComponents.streetNumber = component.long_name
                                        break
                                    case 'route':
                                        addressComponents.address = component.long_name
                                        break
                                    case 'locality':
                                        addressComponents.city = component.long_name
                                        break
                                    case 'administrative_area_level_1':
                                        addressComponents.state = component.short_name
                                        break
                                    case 'postal_code':
                                        addressComponents.zipCode = component.long_name
                                        break
                                }
                            })
                        })

                        const locations = cloneDeep(this.formData.locations)
                        locations[index].city = addressComponents.city || ''
                        locations[index].state = addressComponents.state || ''
                        locations[index].zip = addressComponents.zipCode || ''
                        locations[index].address = `${addressComponents.streetNumber || ''} ${addressComponents.address || ''}`
                        this.$set(this.formData, 'locations', locations)
                        this.isSuggested = true
                    }
                })
            },
        },
    }
</script>
